/**
 * useSignIn Hook
 * 
 * Manages sign-in form state and authentication flow.
 */

import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GlobalContext } from '../../../app/providers/GlobalProvider';
import authApi from '../services/authApi';
import { useAuth } from './useAuth';
import { ROUTES } from '../../../shared/utils/constants';
import { primeServerTimeZone } from '../../../shared/hooks/useServerTimeZone';
import { setInitialLoad } from '../../../store/slices/dashboardSlice';
import { clearOrchestrationCache } from '../../../store/slices/orchestrationSlice';

/**
 * useSignIn hook for managing sign-in flow
 * @returns {Object} Sign-in state and handlers
 */
export function useSignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { storeSession } = useAuth();
  const { fetchProjects, fetchPendingRequests, setOrchestrations } = useContext(GlobalContext);

  // Pre-fill email and password from navigation state
  useEffect(() => {
    if (location.state?.prefillEmail) {
      setEmail(location.state.prefillEmail);
    }
    if (location.state?.prefillPassword) {
      setPassword(location.state.prefillPassword);
    }
  }, [location.state]);

  /**
   * Handle sign-in form submission
   * @param {Event} e - Form submit event
   */
  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // CRITICAL: Clear all cached data BEFORE login to ensure clean state
      // This prevents data from previous organization from persisting
      dispatch(clearOrchestrationCache());
      if (setOrchestrations) {
        setOrchestrations([]);
      }

      // Fetch server timezone (quick operation, done before navigation) and
      // seed the useServerTimeZone cache so consumers never need to refetch.
      try {
        const timezoneResponse = await authApi.getTimezone();
        if (timezoneResponse?.timeZone) {
          primeServerTimeZone(timezoneResponse.timeZone);
        }
      } catch (timezoneError) {
        console.error('Error fetching server timezone:', timezoneError);
        // Continue with login even if timezone fetch fails
      }

      // Attempt login
      const response = await authApi.login({ email, password });

      if (response.success && response.data) {
        // Store session data (this also clears sessionStorage caches)
        storeSession(response.data);

        // Set initial load state to show loader after navigation
        dispatch(setInitialLoad(true));

        // Land on Workflows. The workflow is the product’s focal point — the
        // surface that answers “what needs me?” — and the workspace is one
        // click away for anyone who came to fire a request.
        navigate(ROUTES.WORKFLOW);

        // Background data loading (non-blocking)
        setTimeout(async () => {
          // Fetch pending requests in background
          try {
            await fetchPendingRequests();
          } catch (fetchError) {
            console.error('Error fetching pending requests:', fetchError);
          }

          // Fetch projects in background
          try {
            await fetchProjects();
          } catch (fetchError) {
            console.error('Error fetching projects:', fetchError);
          }
        }, 0);
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    setError(null);
  };

  return {
    email,
    password,
    isLoading,
    error,
    setEmail,
    setPassword,
    handleSignIn,
    clearError,
  };
}

export default useSignIn;
